// ---------------------------------------------------------------------------
// SEO landing-page configuration for the /child-voices/ hierarchy.
// One reusable template (ChildVoiceLanguage.jsx) is driven entirely by this
// data. Improve copy per language here without touching the directory code.
//
// Each entry:
//   slug         -> canonical URL segment (/child-voices/<slug>)
//   language     -> EXACT database `language` value used to filter the directory
//                   (null/"" would show everything — always set a real value)
//   displayName  -> short label used in breadcrumbs / chips
//   h1           -> page H1
//   title        -> <title> (SEO.jsx appends " | VoiceOverKids")
//   description  -> meta description (unique per language)
//   intro        -> short unique paragraph under the H1
//   extra        -> optional additional SEO paragraph beneath the results
//   related      -> slugs of closely related variants for contextual linking
// ---------------------------------------------------------------------------

export const CHILD_VOICES = {
  "american-english": {
    language: "American English", displayName: "American English",
    h1: "American English Kids Voice Over Artists",
    title: "American English Kids Voice Over Artists",
    description: "Cast professional American English child voice over artists. Listen to US kids voice samples for commercials, animation, e-learning and games.",
    intro: "Discover professional American English child voice over artists for national commercials, animation, apps, e-learning and games. Every young voice below is a real, directed child actor — listen to their demos and shortlist the right fit for your production.",
    extra: "Our American English kids voice talent covers a wide range of ages and styles, from bright, upbeat commercial reads to natural conversational narration and authentic character voices for animation and gaming.",
    related: ["british-english", "australian-english"],
  },
  "british-english": {
    language: "British English", displayName: "British English",
    h1: "British English Kids Voice Over Artists",
    title: "British English Kids Voice Over Artists",
    description: "Find professional British English child voice actors. Listen to UK kids voice over demos for commercials, animation, audiobooks and e-learning.",
    intro: "Browse authentic British English child voice actors with natural UK accents for commercials, animation, audiobooks, games and educational content. Preview each young performer's demos and build your shortlist in minutes.",
    extra: "From received pronunciation to regional UK accents, our British kids voice over artists bring warmth and clarity to brand films, character work and long-form narration alike.",
    related: ["american-english", "australian-english"],
  },
  "australian-english": {
    language: "Australian English", displayName: "Australian English",
    h1: "Australian English Kids Voice Over Artists",
    title: "Australian English Kids Voice Over Artists",
    description: "Cast Australian English child voice over talent. Listen to genuine Aussie kids voice samples for commercials, animation, games and e-learning.",
    intro: "Meet genuine Australian English child voice over talent with relaxed, natural Aussie accents for commercials, animation, games and learning content. Listen to demos and shortlist the voices that suit your brief.",
    extra: "Our Australian kids voices are perfect for local campaigns and international productions that need an authentic down-under sound from real young performers.",
    related: ["american-english", "british-english"],
  },
  "french": {
    language: "French", displayName: "French",
    h1: "French Kids Voice Over Artists",
    title: "French Kids Voice Over Artists",
    description: "Cast professional French child voice over artists. Listen to French kids voice samples for commercials, animation, e-learning and dubbing.",
    intro: "Discover professional French child voice over artists for commercials, animation, e-learning, games and dubbing. Listen to demos from our French kids voice talent and find the right young voice for your project.",
    extra: "Whether you need a playful character voice for animation, a sincere read for a brand film or clean narration for e-learning, our French young voice talent delivers directed, broadcast-ready performances.",
    related: ["belgian-flemish"],
  },
  "german": {
    language: "German", displayName: "German",
    h1: "German Kids Voice Over Artists",
    title: "German Kids Voice Over Artists",
    description: "Find professional German child voice actors. Listen to German kids voice over demos for commercials, animation, dubbing, games and e-learning.",
    intro: "Cast authentic German child voice actors for commercials, animation, dubbing, games and educational productions. Preview demos from our German kids voice talent and shortlist the perfect young voice.",
    extra: "Our German-speaking child voices work across TV and radio campaigns, character animation and localisation projects, always recorded with professional direction.",
    related: [],
  },
  "spanish": {
    language: "Spanish", displayName: "Spanish",
    h1: "Spanish Kids Voice Over Artists",
    title: "Spanish Kids Voice Over Artists",
    description: "Cast European Spanish child voice over artists. Listen to Spanish kids voice samples for commercials, animation, games, dubbing and e-learning.",
    intro: "Discover European (Castilian) Spanish child voice over artists for commercials, animation, games, dubbing and e-learning. Listen to demos from our Spanish kids voice talent and cast with confidence.",
    extra: "Looking for a Latin American sound instead? We also represent dedicated Latin American Spanish young voices for productions aimed at audiences across the Americas.",
    related: ["latin-american-spanish"],
  },
  "latin-american-spanish": {
    language: "Latin American Spanish", displayName: "Latin American Spanish",
    h1: "Latin American Spanish Kids Voice Over Artists",
    title: "Latin American Spanish Kids Voice Over Artists",
    description: "Find Latin American Spanish child voice actors. Listen to neutral Latin Spanish kids voice demos for commercials, animation, dubbing and e-learning.",
    intro: "Cast Latin American Spanish child voice actors with neutral and regional accents for commercials, animation, dubbing and e-learning across the Americas. Preview demos and shortlist the right young voice.",
    extra: "For campaigns targeting Spain, explore our European Spanish kids voice talent for an authentic Castilian sound.",
    related: ["spanish"],
  },
  "italian": {
    language: "Italian", displayName: "Italian",
    h1: "Italian Kids Voice Over Artists",
    title: "Italian Kids Voice Over Artists",
    description: "Cast professional Italian child voice over artists. Listen to Italian kids voice samples for commercials, animation, dubbing, games and e-learning.",
    intro: "Browse professional Italian child voice over artists for commercials, animation, dubbing, games and learning content. Listen to demos from our Italian kids voice talent and find your ideal young voice.",
    extra: "Our Italian young performers bring expressive, natural delivery to brand campaigns, character animation and localisation projects.",
    related: [],
  },
  "portuguese-portugal": {
    language: "Portuguese (Portugal)", displayName: "European Portuguese",
    h1: "European Portuguese Kids Voice Over Artists",
    title: "European Portuguese Kids Voice Over Artists",
    description: "Cast European Portuguese child voice over artists. Listen to Portugal kids voice samples for commercials, animation, dubbing, games and e-learning.",
    intro: "Discover European Portuguese child voice over artists from Portugal for commercials, animation, dubbing, games and e-learning. Listen to demos and shortlist the right young voice for your production.",
    extra: "Need a Brazilian sound instead? We also represent dedicated Brazilian Portuguese kids voice talent for productions aimed at Brazil.",
    related: ["portuguese-brazil"],
  },
  "portuguese-brazil": {
    language: "Portuguese (Brazil)", displayName: "Brazilian Portuguese",
    h1: "Brazilian Portuguese Kids Voice Over Artists",
    title: "Brazilian Portuguese Kids Voice Over Artists",
    description: "Find Brazilian Portuguese child voice actors. Listen to Brazil kids voice over demos for commercials, animation, dubbing, games and e-learning.",
    intro: "Cast Brazilian Portuguese child voice actors for commercials, animation, dubbing, games and e-learning aimed at audiences in Brazil. Preview demos from our young voice talent and build your shortlist.",
    extra: "For productions targeting Portugal, explore our European Portuguese kids voice artists for an authentic continental sound.",
    related: ["portuguese-portugal"],
  },
  "dutch": {
    language: "Dutch", displayName: "Dutch",
    h1: "Dutch Kids Voice Over Artists",
    title: "Dutch Kids Voice Over Artists",
    description: "Cast professional Dutch child voice over artists. Listen to Netherlands kids voice samples for commercials, animation, dubbing, games and e-learning.",
    intro: "Browse professional Dutch child voice over artists from the Netherlands for commercials, animation, dubbing, games and e-learning. Listen to demos from our Dutch kids voice talent — our largest voice pool — and cast with ease.",
    extra: "For a Belgian sound, discover our Belgian Flemish young voices recorded with authentic regional pronunciation.",
    related: ["belgian-flemish"],
  },
  "belgian-flemish": {
    language: "Belgian Flemish", displayName: "Belgian Flemish",
    h1: "Belgian Flemish Kids Voice Over Artists",
    title: "Belgian Flemish Kids Voice Over Artists",
    description: "Find Belgian Flemish child voice actors. Listen to Flemish kids voice over demos for commercials, animation, dubbing, games and e-learning.",
    intro: "Cast Belgian Flemish child voice actors with authentic Flemish pronunciation for commercials, animation, dubbing and e-learning in Belgium. Preview demos and shortlist the right young voice.",
    extra: "For the Netherlands market, explore our wider pool of Dutch kids voice over artists.",
    related: ["dutch"],
  },
  "greek": {
    language: "Greek", displayName: "Greek",
    h1: "Greek Kids Voice Over Artists",
    title: "Greek Kids Voice Over Artists",
    description: "Cast Greek child voice over artists. Listen to Greek kids voice samples for commercials, animation, dubbing, games and e-learning.",
    intro: "Discover Greek child voice over artists for commercials, animation, dubbing, games and e-learning. Listen to demos from our Greek kids voice talent and find the right young voice for your project.",
    extra: "Our Greek young performers deliver natural, directed reads for brand campaigns, character work and localisation.",
    related: [],
  },
  "danish": {
    language: "Danish", displayName: "Danish",
    h1: "Danish Kids Voice Over Artists",
    title: "Danish Kids Voice Over Artists",
    description: "Cast Danish child voice over talent. Request Danish kids voices for commercials, animation, dubbing, games and e-learning from our network.",
    intro: "Looking for Danish child voice over talent for commercials, animation, dubbing, games or e-learning? Our international network reaches far beyond the voices shown online — tell us your brief and we'll cast the right young Danish voice for you.",
    extra: "We regularly source Danish kids voices for Scandinavian and international productions. Send us your script and we'll propose suitable young talent.",
    related: ["swedish", "norwegian"],
  },
  "finnish": {
    language: "Finnish", displayName: "Finnish",
    h1: "Finnish Kids Voice Over Artists",
    title: "Finnish Kids Voice Over Artists",
    description: "Cast Finnish child voice over artists. Listen to Finnish kids voice samples for commercials, animation, dubbing, games and e-learning.",
    intro: "Browse Finnish child voice over artists for commercials, animation, dubbing, games and e-learning. Listen to demos from our Finnish kids voice talent and shortlist the perfect young voice.",
    extra: "Our Finnish young performers bring authentic, natural delivery to Nordic and international campaigns.",
    related: ["swedish", "norwegian"],
  },
  "norwegian": {
    language: "Norwegian", displayName: "Norwegian",
    h1: "Norwegian Kids Voice Over Artists",
    title: "Norwegian Kids Voice Over Artists",
    description: "Cast Norwegian child voice over artists. Listen to Norwegian kids voice samples for commercials, animation, dubbing, games and e-learning.",
    intro: "Discover Norwegian child voice over artists for commercials, animation, dubbing, games and e-learning. Listen to demos from our Norwegian kids voice talent and cast the right young voice.",
    extra: "Our Norwegian young voices are ideal for Scandinavian localisation and international productions needing an authentic Nordic sound.",
    related: ["swedish", "finnish"],
  },
  "swedish": {
    language: "Swedish", displayName: "Swedish",
    h1: "Swedish Kids Voice Over Artists",
    title: "Swedish Kids Voice Over Artists",
    description: "Cast Swedish child voice over artists. Listen to Swedish kids voice samples for commercials, animation, dubbing, games and e-learning.",
    intro: "Browse Swedish child voice over artists for commercials, animation, dubbing, games and e-learning. Listen to demos from our Swedish kids voice talent and shortlist the right young voice.",
    extra: "Our Swedish young performers deliver natural, directed reads for Nordic and global campaigns.",
    related: ["norwegian", "finnish"],
  },
  "polish": {
    language: "Polish", displayName: "Polish",
    h1: "Polish Kids Voice Over Artists",
    title: "Polish Kids Voice Over Artists",
    description: "Cast Polish child voice over talent. Request Polish kids voices for commercials, animation, dubbing, games and e-learning from our network.",
    intro: "Need Polish child voice over talent for commercials, animation, dubbing, games or e-learning? Our international network reaches beyond the voices shown online — send us your brief and we'll cast the right young Polish voice.",
    extra: "We regularly source Polish kids voices for European and international productions. Share your script and we'll propose suitable young talent.",
    related: ["russian", "romanian"],
  },
  "russian": {
    language: "Russian", displayName: "Russian",
    h1: "Russian Kids Voice Over Artists",
    title: "Russian Kids Voice Over Artists",
    description: "Cast Russian child voice over artists. Listen to Russian kids voice samples for commercials, animation, dubbing, games and e-learning.",
    intro: "Discover Russian child voice over artists for commercials, animation, dubbing, games and e-learning. Listen to demos from our Russian kids voice talent and find the right young voice.",
    extra: "Our Russian young performers deliver expressive, directed reads for character animation, localisation and brand work.",
    related: [],
  },
  "romanian": {
    language: "Romanian", displayName: "Romanian",
    h1: "Romanian Kids Voice Over Artists",
    title: "Romanian Kids Voice Over Artists",
    description: "Cast Romanian child voice over artists. Listen to Romanian kids voice samples for commercials, animation, dubbing, games and e-learning.",
    intro: "Browse Romanian child voice over artists for commercials, animation, dubbing, games and e-learning. Listen to demos from our Romanian kids voice talent and shortlist the perfect young voice.",
    extra: "Our Romanian young voices bring natural, authentic delivery to regional and international productions.",
    related: [],
  },
  "turkish": {
    language: "Turkish", displayName: "Turkish",
    h1: "Turkish Kids Voice Over Artists",
    title: "Turkish Kids Voice Over Artists",
    description: "Cast Turkish child voice over artists. Listen to Turkish kids voice samples for commercials, animation, dubbing, games and e-learning.",
    intro: "Discover Turkish child voice over artists for commercials, animation, dubbing, games and e-learning. Listen to demos from our Turkish kids voice talent and cast the right young voice.",
    extra: "Our Turkish young performers deliver natural, directed reads for brand campaigns, character work and localisation.",
    related: [],
  },
  "arabic": {
    language: "Arabic", displayName: "Arabic",
    h1: "Arabic Kids Voice Over Artists",
    title: "Arabic Kids Voice Over Artists",
    description: "Cast Arabic child voice over artists. Listen to Arabic kids voice samples for commercials, animation, dubbing, games and e-learning.",
    intro: "Browse Arabic child voice over artists for commercials, animation, dubbing, games and e-learning. Listen to demos from our Arabic kids voice talent and shortlist the right young voice for your production.",
    extra: "Our Arabic young performers cover Modern Standard Arabic and regional needs for broadcast, character animation and localisation projects.",
    related: [],
  },
  "hebrew": {
    language: "Hebrew", displayName: "Hebrew",
    h1: "Hebrew Kids Voice Over Artists",
    title: "Hebrew Kids Voice Over Artists",
    description: "Cast Hebrew child voice over talent. Request Israeli Hebrew kids voices for commercials, animation, dubbing, games and e-learning from our network.",
    intro: "Looking for Hebrew child voice over talent for commercials, animation, dubbing, games or e-learning? Our international network reaches beyond the voices shown online — send us your brief and we'll cast the right young Hebrew voice.",
    extra: "We regularly source Israeli Hebrew kids voices for regional and international productions. Share your script and we'll propose suitable young talent.",
    related: [],
  },
  "afrikaans": {
    language: "Afrikaans", displayName: "Afrikaans",
    h1: "Afrikaans Kids Voice Over Artists",
    title: "Afrikaans Kids Voice Over Artists",
    description: "Cast Afrikaans child voice over artists. Listen to South African Afrikaans kids voice samples for commercials, animation, games and e-learning.",
    intro: "Discover Afrikaans child voice over artists from South Africa for commercials, animation, games and e-learning. Listen to demos from our Afrikaans kids voice talent and shortlist the right young voice.",
    extra: "Our Afrikaans young performers deliver warm, natural reads for local campaigns and international productions.",
    related: [],
  },
  "chinese": {
    language: "Chinese", displayName: "Chinese",
    h1: "Chinese Kids Voice Over Artists",
    title: "Chinese Kids Voice Over Artists",
    description: "Cast Chinese (Mandarin) child voice over artists. Listen to Chinese kids voice samples for commercials, animation, dubbing, games and e-learning.",
    intro: "Browse Chinese (Mandarin) child voice over artists for commercials, animation, dubbing, games and e-learning. Listen to demos from our Chinese kids voice talent and find the right young voice.",
    extra: "Our Chinese young performers bring authentic, natural delivery to brand campaigns, character animation and localisation for Asian and global audiences.",
    related: ["japanese", "vietnamese"],
  },
  "japanese": {
    language: "Japanese", displayName: "Japanese",
    h1: "Japanese Kids Voice Over Artists",
    title: "Japanese Kids Voice Over Artists",
    description: "Cast Japanese child voice over artists. Listen to Japanese kids voice samples for commercials, animation, dubbing, games and e-learning.",
    intro: "Discover Japanese child voice over artists for commercials, animation, dubbing, games and e-learning. Listen to demos from our Japanese kids voice talent and cast the right young voice.",
    extra: "Our Japanese young performers deliver expressive, directed reads perfect for animation, games and brand localisation.",
    related: ["chinese", "vietnamese"],
  },
  "vietnamese": {
    language: "Vietnamese", displayName: "Vietnamese",
    h1: "Vietnamese Kids Voice Over Artists",
    title: "Vietnamese Kids Voice Over Artists",
    description: "Cast Vietnamese child voice over artists. Listen to Vietnamese kids voice samples for commercials, animation, dubbing, games and e-learning.",
    intro: "Browse Vietnamese child voice over artists for commercials, animation, dubbing, games and e-learning. Listen to demos from our Vietnamese kids voice talent and shortlist the right young voice.",
    extra: "Our Vietnamese young performers bring natural, authentic delivery to regional and international productions.",
    related: ["chinese", "japanese"],
  },
};

// Ordered list of slugs for the "Browse by language" internal-linking section.
export const CHILD_VOICE_ORDER = Object.keys(CHILD_VOICES);

export const getChildVoice = (slug) => CHILD_VOICES[slug] || null;

export const childVoicePath = (slug) => `/child-voices/${slug}`;

// Mirror of backend slugify (server.py) so profile/language URLs match the API.
export const slugify = (s) =>
  (s || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

// Clean, SEO-friendly voice profile path: /voices/<language>/<name>
export const voicePath = (t) => `/voices/${slugify(t.language)}/${slugify(t.name)}`;
